import { fetchWithCSRF } from "@/helpers/common/csrf";
import clsx from "clsx";
import {
    ChangeEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { FiUploadCloud } from "react-icons/fi";

const API_URL = import.meta.env.VITE_VANDERHEIM_NINJA_API_URL;
const TAG_IMAGES_URL = `${API_URL}/longhouse-api/village/tag-images/`;
const TAGS_REPO_URL = "https://github.com/vanderheim-village/vanderheim-tags";
const SIMULTANEOUS_UPLOADS = 4;

const SLUG_PATTERN = /^[a-z0-9_-]+$/;
const FILENAME_PATTERN = /^tier_\d+(_\d+)?\.png$/;

interface TagFolder {
    slug: string;
    files: string[];
}

export interface TagImage {
    file: File;
    slug: string;
    filename: string;
}

export interface UploadPlan {
    newImages: TagImage[];
    existingImages: TagImage[];
    misplaced: string[];
}

interface Failure {
    image: TagImage;
    reason: string;
}

const imagePath = ({ slug, filename }: TagImage) => `${slug}/${filename}`;

// Each PNG belongs to the folder it sits in, so one tag and a whole unzipped
// repo both work. Anything else in that repo, like scripts and .git, is skipped.
export function planUpload(files: File[], existing: Set<string>): UploadPlan {
    const plan: UploadPlan = {
        newImages: [],
        existingImages: [],
        misplaced: [],
    };
    for (const file of files) {
        const parts = (file.webkitRelativePath || file.name).split("/");
        const filename = parts[parts.length - 1];
        const isHidden = parts.some((part) => part.startsWith("."));
        if (isHidden || !filename.toLowerCase().endsWith(".png")) {
            continue;
        }
        const slug = parts[parts.length - 2] ?? "";
        if (!SLUG_PATTERN.test(slug) || !FILENAME_PATTERN.test(filename)) {
            plan.misplaced.push(parts.join("/"));
            continue;
        }
        const image = { file, slug, filename };
        if (existing.has(imagePath(image))) {
            plan.existingImages.push(image);
        } else {
            plan.newImages.push(image);
        }
    }
    return plan;
}

async function uploadImage(image: TagImage, replace: boolean): Promise<void> {
    const body = new FormData();
    body.append("slug", image.slug);
    body.append("replace", String(replace));
    body.append("file", image.file, image.filename);

    const response = await fetchWithCSRF(TAG_IMAGES_URL, {
        method: "POST",
        body,
    });
    if (!response.ok) {
        const { detail } = await response.json().catch(() => ({ detail: "" }));
        throw new Error(detail || `HTTP ${response.status}`);
    }
}

export default function TagImagesPage() {
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [tags, setTags] = useState<TagFolder[] | null>(null);
    const [files, setFiles] = useState<File[]>([]);
    const [replace, setReplace] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState({ done: 0, total: 0 });
    const [failures, setFailures] = useState<Failure[]>([]);
    const [failed, setFailed] = useState(false);

    const load = useCallback(async () => {
        try {
            const response = await fetchWithCSRF(TAG_IMAGES_URL);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            const data = await response.json();
            setTags(data.tags);
            setFailed(false);
        } catch (error) {
            console.error("Error loading tag images:", error);
            setFailed(true);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const plan = useMemo(() => {
        const existing = new Set(
            (tags ?? []).flatMap(({ slug, files: names }) =>
                names.map((name) => `${slug}/${name}`),
            ),
        );
        return planUpload(files, existing);
    }, [files, tags]);

    const selected = replace
        ? [...plan.newImages, ...plan.existingImages]
        : plan.newImages;

    const chooseFolder = (event: ChangeEvent<HTMLInputElement>) => {
        setFiles(Array.from(event.target.files ?? []));
        setFailures([]);
        setProgress({ done: 0, total: 0 });
    };

    const clearFolder = () => {
        setFiles([]);
        setFailures([]);
        setProgress({ done: 0, total: 0 });
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const upload = async (images: TagImage[]) => {
        setUploading(true);
        setFailures([]);
        setProgress({ done: 0, total: images.length });

        const queue = [...images];
        const failedImages: Failure[] = [];
        const worker = async () => {
            for (let image = queue.shift(); image; image = queue.shift()) {
                try {
                    await uploadImage(image, replace);
                } catch (error) {
                    failedImages.push({
                        image,
                        reason:
                            error instanceof Error
                                ? error.message
                                : "Upload failed",
                    });
                }
                setProgress((current) => ({
                    ...current,
                    done: current.done + 1,
                }));
            }
        };
        await Promise.all(Array.from({ length: SIMULTANEOUS_UPLOADS }, worker));

        setFailures(failedImages);
        setUploading(false);
        await load();
    };

    const finished =
        !uploading && progress.total > 0 && progress.done === progress.total;
    const percent = progress.total
        ? Math.round((progress.done / progress.total) * 100)
        : 0;
    const folderName = files[0]?.webkitRelativePath.split("/")[0];

    return (
        <div className="mx-auto mt-4 w-full px-4">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                {/* LEFT: Upload Form */}
                <div className="rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                    <h1 className="mb-4 text-2xl font-bold text-slate-900 dark:text-slate-100">
                        Upload Tag Images
                    </h1>

                    <div className="relative mb-4">
                        <div
                            className={clsx(
                                "flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 transition-colors duration-150",
                                files.length > 0
                                    ? "border-brand bg-brand/10 hover:bg-brand/20 dark:bg-brand/10 dark:hover:bg-brand/20"
                                    : "border-slate-300 hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800",
                            )}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <FiUploadCloud className="h-10 w-10 text-slate-600 dark:text-slate-300" />
                            <p className="text-sm text-slate-700 dark:text-slate-400">
                                {files.length > 0
                                    ? folderName
                                    : "Click to select a folder of PNGs named by tier (e.g., tier_1.png, tier_1_2.png)."}
                            </p>
                        </div>

                        {files.length > 0 && !uploading && (
                            <button
                                onClick={clearFolder}
                                className="absolute top-2 right-2 cursor-pointer text-sm text-slate-600 hover:text-red-500 dark:text-slate-300"
                                title="Clear folder"
                                type="button"
                            >
                                ×
                            </button>
                        )}

                        <input
                            ref={(input) => {
                                fileInputRef.current = input;
                                input?.setAttribute("webkitdirectory", "");
                            }}
                            type="file"
                            multiple
                            aria-label="Folder"
                            disabled={uploading}
                            onChange={chooseFolder}
                            className="hidden"
                        />
                    </div>

                    {files.length > 0 && (
                        <div className="mb-4 space-y-3">
                            <p className="text-sm text-slate-700 dark:text-slate-400">
                                {plan.newImages.length} new •{" "}
                                {plan.existingImages.length} already uploaded •{" "}
                                {plan.misplaced.length} can't be uploaded
                            </p>

                            {plan.misplaced.length > 0 && (
                                <div className="rounded-lg bg-yellow-100 p-4 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400">
                                    <p className="mb-1 font-semibold">
                                        Can't be uploaded
                                    </p>
                                    <ul className="max-h-40 overflow-y-auto text-sm">
                                        {plan.misplaced.map((path) => (
                                            <li key={path}>{path}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                                <input
                                    type="checkbox"
                                    checked={replace}
                                    disabled={uploading}
                                    onChange={(event) =>
                                        setReplace(event.target.checked)
                                    }
                                />
                                Replace existing images
                            </label>
                            {replace && (
                                <p className="text-xs text-slate-600 dark:text-slate-400">
                                    Replaced images can stay cached for up to 4
                                    hours.
                                </p>
                            )}
                        </div>
                    )}

                    <button
                        type="button"
                        disabled={uploading || !tags || selected.length === 0}
                        onClick={() => upload(selected)}
                        className={clsx(
                            "w-full rounded-md px-4 py-2 font-medium transition-colors",
                            uploading || !tags || selected.length === 0
                                ? "cursor-not-allowed bg-slate-300 text-slate-500 dark:bg-slate-800 dark:text-slate-500"
                                : "bg-brand hover:bg-brand/90 cursor-pointer text-white",
                        )}
                    >
                        {uploading
                            ? "Uploading..."
                            : selected.length === 0
                              ? "Upload"
                              : `Upload ${selected.length} ${selected.length === 1 ? "image" : "images"}`}
                    </button>

                    {progress.total > 0 && (
                        <div className="mt-3">
                            <div
                                role="progressbar"
                                aria-label="Upload progress"
                                aria-valuenow={percent}
                                className="h-2 w-full rounded bg-slate-200 dark:bg-slate-700"
                            >
                                <div
                                    className="bg-brand h-2 rounded"
                                    style={{ width: `${percent}%` }}
                                />
                            </div>
                            <p className="mt-2 text-xs text-slate-600 dark:text-slate-400">
                                {progress.done} of {progress.total}
                                {finished && ` done, ${failures.length} failed`}
                            </p>
                        </div>
                    )}

                    {failed && (
                        <div className="mt-4 rounded-lg bg-red-100 p-4 text-red-800 dark:bg-red-900/20 dark:text-red-400">
                            <p className="mb-1 font-semibold">Error</p>
                            <p className="text-sm">
                                Something went wrong talking to the server.
                            </p>
                        </div>
                    )}

                    {failures.length > 0 && (
                        <div className="mt-4 rounded-lg bg-yellow-100 p-4 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400">
                            <p className="mb-1 font-semibold">Failed</p>
                            <ul className="max-h-60 overflow-y-auto text-sm">
                                {failures.map(({ image, reason }) => (
                                    <li key={imagePath(image)}>
                                        {imagePath(image)}: {reason}
                                    </li>
                                ))}
                            </ul>
                            <button
                                type="button"
                                disabled={uploading}
                                onClick={() =>
                                    upload(failures.map(({ image }) => image))
                                }
                                className="mt-3 cursor-pointer text-sm font-semibold hover:underline"
                            >
                                Retry failed uploads
                            </button>
                        </div>
                    )}
                </div>

                {/* RIGHT: Docs Panel */}
                <div className="rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                    <h2 className="mb-4 text-2xl font-bold text-slate-900 dark:text-slate-100">
                        How to Add Tag Images
                    </h2>
                    <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-400">
                        1. Put each tag's PNGs in a folder named after the tag
                        (e.g., <code>sif</code>).
                        <br />
                        2. Commit them to the{" "}
                        <a
                            href={TAGS_REPO_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-brand font-medium hover:underline"
                        >
                            vanderheim-tags
                        </a>{" "}
                        repo, which is the backup.
                        <br />
                        3. Select that folder here. A folder of tag folders,
                        such as an unzipped copy of the repo, works too.
                        <br />
                        4. Upload. Images go live straight away and existing
                        ones are skipped unless you tick{" "}
                        <strong>“Replace existing images”</strong>.
                    </p>

                    <h3 className="mt-6 mb-2 text-lg font-bold text-slate-900 dark:text-slate-100">
                        Already Uploaded
                    </h3>
                    {tags && tags.length === 0 && (
                        <p className="text-sm text-slate-700 dark:text-slate-400">
                            No tag images yet.
                        </p>
                    )}
                    <ul className="grid gap-1 text-sm text-slate-700 sm:grid-cols-2 lg:grid-cols-3 dark:text-slate-400">
                        {tags?.map(({ slug, files: names }) => (
                            <li key={slug}>
                                {slug}: {names.length}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
}
